import { type QueryKey, type UseMutationOptions, useMutation, useQueryClient } from "@tanstack/react-query"
import type { TFunction } from "i18next"
import { useTranslation } from "react-i18next"
import { toast } from "sonner"
import { type ApiError, isApiError } from "@/api/client"

/** Human-friendly, translated description of any error thrown by the API layer. */
export function describeError(error: unknown, t: TFunction): string {
  if (isApiError(error)) {
    if (error.isNetwork) return t("errors.network")
    if (error.isRateLimited) return t("errors.rateLimited")
    if (error.isUnauthorized) return error.message || t("errors.unauthorized")
    if (error.isForbidden) return error.message || t("errors.forbidden")
    if (error.status >= 500) return t("errors.server")
    return error.message || t("errors.unknown")
  }
  if (error instanceof Error && error.message) return error.message
  return t("errors.unknown")
}

type Resolver<TData, TVars, R> = R | ((data: TData, vars: TVars) => R)

export interface ApiMutationOptions<TData, TVars, TContext = unknown>
  extends UseMutationOptions<TData, ApiError, TVars, TContext> {
  /** Query keys (prefixes) to invalidate after success. */
  invalidates?: Resolver<TData, TVars, QueryKey[]>
  /** Toast shown on success. `false`/omitted = no toast. */
  successToast?: Resolver<TData, TVars, string | false | undefined>
  /**
   * Error toast behaviour:
   * - `true` (default): toast the translated error message
   * - `"unlessFields"`: skip the toast for 422 errors with a `fields` map
   *   (use with {@link applyApiErrorToForm} so the errors show inline)
   * - `false`: never toast
   */
  errorToast?: boolean | "unlessFields"
}

function resolve<TData, TVars, R>(r: Resolver<TData, TVars, R>, data: TData, vars: TVars): R {
  return typeof r === "function" ? (r as (d: TData, v: TVars) => R)(data, vars) : r
}

/**
 * `useMutation` with the app's conventions baked in: typed {@link ApiError},
 * cache invalidation by key prefix, and success/error toasts.
 */
export function useApiMutation<TData = void, TVars = void, TContext = unknown>({
  invalidates,
  successToast,
  errorToast = true,
  onSuccess,
  onError,
  ...options
}: ApiMutationOptions<TData, TVars, TContext>) {
  const qc = useQueryClient()
  const { t } = useTranslation()
  return useMutation<TData, ApiError, TVars, TContext>({
    ...options,
    onSuccess: async (data, vars, ctx, mctx) => {
      if (invalidates) {
        const keys = resolve(invalidates, data, vars)
        await Promise.all(keys.map((queryKey) => qc.invalidateQueries({ queryKey })))
      }
      if (successToast) {
        const msg = resolve(successToast, data, vars)
        if (msg) toast.success(msg)
      }
      await onSuccess?.(data, vars, ctx, mctx)
    },
    onError: (error, vars, ctx, mctx) => {
      const skip =
        errorToast === false || (errorToast === "unlessFields" && isApiError(error) && !!error.fields)
      if (!skip) toast.error(describeError(error, t))
      onError?.(error, vars, ctx, mctx)
    },
  })
}
